import React, { ChangeEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'clsx';
import FilterBarSelectInput from 'components/@common/FilterBar/FilterBarSelectInput';
import { flatten, flow, map, partition } from 'lodash/fp';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from '@material-ui/core/styles';
import { Organization } from 'types/schema-generated-types';

const useStyles = makeStyles(theme => ({
  filterContainer: {
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    paddingTop: theme.spacing() * 1.5,
    paddingBottom: theme.spacing() * 1.5,
    boxShadow: theme.shadows[2],
    background: 'white'
  },
  rootOrgLabel: {
    fontWeight: theme.typography.fontWeightBold
  },
  select: {
    width: '100%'
  }
}));

interface ProductLicenseUsageFilterBarProps {
  onChange: (organizationId: number) => void;
  organizations: Organization[];
  selectedOrganizationId?: number | null;
}

const ProductLicenseUsageFilterBar: React.FC<ProductLicenseUsageFilterBarProps> = ({
  onChange,
  organizations = [],
  selectedOrganizationId
}) => {
  const classes = useStyles();
  const { t } = useTranslation('UserManagementFilterBar');

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange(+event.target.value);
  };

  const organizationMenuOptions = useMemo(
    () =>
      organizations.length ? (
        flow(
          partition((org: Organization) => !org.parentOrganizationId),
          flatten,
          map(org => (
            <MenuItem
              key={`${org.type}-${org.id}-option`}
              className={cx({ [classes.rootOrgLabel]: !org.parentOrganizationId })}
              value={org.id}
            >
              {org.name}
            </MenuItem>
          ))
        )(organizations)
      ) : (
        <MenuItem disabled>No organizations available</MenuItem>
      ),
    [classes.rootOrgLabel, organizations]
  );

  return (
    <div className={classes.filterContainer}>
      <Grid container spacing={3}>
        <Grid xs={12} md={3} item>
          <FilterBarSelectInput
            inputProps={{
              'aria-label': t('Organization')
            }}
            formControlProps={{
              className: classes.select
            }}
            onChange={handleChange}
            name="organization"
            label={t('Organization')}
            placeholder={t('Organization')}
            value={selectedOrganizationId}
          >
            {organizationMenuOptions}
          </FilterBarSelectInput>
        </Grid>
      </Grid>
    </div>
  );
};

export default ProductLicenseUsageFilterBar;
